import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Stack,
  Divider,
  TextField,
  Avatar
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AddIcon from "@mui/icons-material/Add";
import AddOrganisationAdminModal from "./AddOrganisationAdminModal";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import IconButton from "@mui/material/IconButton";
import AddSOPAdminModal from "./AddSOPAdminModal";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OrganisationSuccessModal from "./OrganisationSuccessModal";
import ViewAllSpocModal from "./ViewAllSpocModal";

interface OrganisationFormData {
  businessName: string;
  entityType: string;
  natureOfBusiness: string;
  gstNumber: string;
  registrationDate: string;
  registeredAddress: string;
  pincode: string;
}

interface AdminData {
  adminUserName: string;
  email: string;
  mobileNumber: string;
}

const OrganisationForm: React.FC = () => {
  // Static SPOC data
  const staticSpocData: AdminData[] = [
    {
      adminUserName: "Amit Singh",
      email: "sample@gmail.com",
      mobileNumber: "9871234865"
    },
    {
      adminUserName: "Priya Sharma",
      email: "priya.sharma@email.com",
      mobileNumber: "9876543210"
    },
    {
      adminUserName: "Rahul Mehta",
      email: "rahul.mehta@domain.com",
      mobileNumber: "9879876543"
    }
  ];
  const [formData, setFormData] = React.useState<OrganisationFormData>({
    businessName: "Tata Income Private Limited",
    entityType: "Individual",
    natureOfBusiness: "Economic Activity",
    gstNumber: "NHJI563CVG043",
    registrationDate: "15 Jul 2025",
    registeredAddress: "Pritam Pura",
    pincode: "110062"
  });

  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);

  const handleInputChange = (field: keyof OrganisationFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
    e.target.value = "";
  };

  const handleFileDelete = () => {
    setUploadedFile(null);
  };

  const [adminModalOpen, setAdminModalOpen] = React.useState(false);
  const [spocModalOpen, setSpocModalOpen] = React.useState(false);
  const [adminData, setAdminData] = React.useState<AdminData | null>(null);
  const [spocDataList, setSpocDataList] = React.useState<AdminData[]>([]);
  const [successModalOpen, setSuccessModalOpen] = React.useState(false);
  const [viewAllSpocModalOpen, setViewAllSpocModalOpen] = React.useState(false);

  const handleAddOrgAdmin = () => {
    setAdminModalOpen(true);
  };

  const handleAdminAdded = (data: AdminData) => {
    setAdminData(data);
    setAdminModalOpen(false);
  };

  const handleAddSpoc = () => {
    setSpocModalOpen(true);
  };

  const handleSpocAdded = (data: AdminData) => {
    // On first add, initialize with staticSpocData only (don't add the new entry)
    if (spocDataList.length === 0) {
      setSpocDataList(staticSpocData);
    }
    setSpocModalOpen(false);
  };

  const handleSubmit = () => {
    setSuccessModalOpen(true);
  };

  const handleRedirect = () => {
    // Redirect to dashboard or desired page
    window.location.href = "/dashboard";
  };

  const handleViewAllSpoc = () => {
    setViewAllSpocModalOpen(true);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F3F4F6", minHeight: "100vh" }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 3,
          color: "#212121",
          fontSize: { xs: 20, md: 24 }
        }}
      >
        Organisation Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column - Basic Details & Documents */}
        <Grid item xs={12} md={6}>
          {/* Basic Details Section */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: "#212121",
                mb: 2.5
              }}
            >
              Basic Details
            </Typography>

            <Stack spacing={2}>
              <DetailRow
                label="Business Name"
                value={formData.businessName}
              />
              <DetailRow
                label="Entity Type"
                value={formData.entityType}
              />
              <DetailRow
                label="Nature of Business"
                value={formData.natureOfBusiness}
              />
              <DetailRow
                label="GST Number"
                value={formData.gstNumber}
              />
              <DetailRow
                label="Registration Date"
                value={formData.registrationDate}
              />
              <DetailRow
                label="Registered Address"
                value={formData.registeredAddress}
              />
              <DetailRow
                label="Pincode"
                value={formData.pincode}
              />
            </Stack>
          </Paper>

          {/* Documents Section */}
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: "#1F2937",
                mb: 2.5
              }}
            >
              Documents
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "flex-start" },
                justifyContent: "space-between",
                gap: { xs: 2, sm: 3 }
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 14,
                  color: "#3A1C73",
                  fontWeight: 500
                }}
              >
                Upload GST Certificate
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", sm: "flex-end" }, minWidth: { xs: "100%", sm: "fit-content" } }}>
                {!uploadedFile && (
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadOutlinedIcon sx={{ color: "#3A1C73" }} />}
                    sx={{
                      textTransform: "none",
                      color: "#3A1C73",
                      borderColor: "#D1D5DB",
                      borderRadius: 2,
                      fontSize: { xs: 13, sm: 16 },
                      fontWeight: 600,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.5 },
                      border: "1px solid #CFDAE7",
                      width: { xs: "100%", sm: "auto" },
                      "&:hover": {
                        borderColor: "#3A1C73",
                        bgcolor: "rgba(58,28,115,0.04)"
                      }
                    }}
                  >
                    Upload Document
                    <input
                      hidden
                      accept=".pdf"
                      type="file"
                      onChange={handleFileUpload}
                    />
                  </Button>
                )}
                {!uploadedFile && (
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 13,
                      color: "#9CA3AF",
                      mt: 1,
                      textAlign: "right"
                    }}
                  >
                    Only Pdf format accepted (with max 20 MB)
                  </Typography>
                )}
                {uploadedFile && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid #D1D5DB",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      gap: 1,
                      width: { xs: "100%", sm: 260 },
                      bgcolor: "#fff"
                    }}
                  >
                    <InsertDriveFileOutlinedIcon sx={{ color: "#9CA3AF", fontSize: 20, flexShrink: 0 }} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#374151",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {uploadedFile.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleFileDelete}
                      sx={{ color: "#EF4444", p: 0.5, flexShrink: 0 }}
                    >
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Organisation Admin & SPOC */}
        <Grid item xs={12} md={6}>
          {/* Organisation Admin Section */}
          <Paper
            sx={{
              borderRadius: "10px",
              p: { xs: 2, sm: 3 },
              mb: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
            }}
          >
            {!adminData ? (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: "#212121"
                  }}
                >
                  Organisation Admin
                </Typography>
                <Button
                  size="small"
                  onClick={handleAddOrgAdmin}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#3A1C73",
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto"
                  }}
                >
                  Add
                </Button>
              </Stack>
            ) : (
              <Stack spacing={0}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: "#212121",
                    mb: 2
                  }}
                >
                  Organisation Admin
                </Typography>
                <Divider sx={{ mb: 2.5, borderStyle: "dashed", borderColor: "#E5E7EB" }} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: "#CAC317",
                        fontSize: 18,
                        fontWeight: 700,
                        flexShrink: 0
                      }}
                    >
                      {adminData.adminUserName.substring(0, 2).toUpperCase()}
                    </Avatar>
                    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 18,
                          color: "#212121",
                          lineHeight: 1.2
                        }}
                      >
                        {adminData.adminUserName} Amit Singh
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#9CA3AF",
                          lineHeight: 1.4,
                          mt: 0.3
                        }}
                      >
                        {adminData.email} amit@gmail.com  | {adminData.mobileNumber} 9871234865
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    sx={{ color: "#3A1C73", p: 0.5, flexShrink: 0 }}
                  >
                    <MoreVertIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                </Box>
              </Stack>
            )}
          </Paper>

          {/* SPOC Section */}
          <Paper
            sx={{
              borderRadius: "10px",
              p: { xs: 2, sm: 3 },
              mb: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
            }}
          >
            {(spocDataList.length === 0) ? (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: "#212121"
                  }}
                >
                  SPOC
                </Typography>
                <Button
                  size="small"
                  onClick={handleAddSpoc}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#3A1C73",
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto"
                  }}
                >
                  Add
                </Button>
              </Stack>
            ) : (
              <Stack spacing={0}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#212121"
                    }}
                  >
                    SPOC
                  </Typography>
                  <Stack direction="row" gap={2}>
                    <Button
                      size="small"
                      onClick={handleAddSpoc}
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#3A1C73",
                        textTransform: "none",
                        p: 0,
                        minWidth: "auto"
                      }}
                    >
                      Add New
                    </Button>
                    <Button
                      size="small"
                      onClick={handleViewAllSpoc}
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#3A1C73",
                        textTransform: "none",
                        p: 0,
                        minWidth: "auto"
                      }}
                    >
                      View All({spocDataList.length})
                    </Button>
                  </Stack>
                </Stack>
                <Divider sx={{ mb: 2, borderStyle: "dashed", borderColor: "#E5E7EB" }} />
                <Stack spacing={2}>
                  {spocDataList.map((spoc, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                        <Avatar
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: "#CAC317",
                            fontSize: 18,
                            fontWeight: 700,
                            flexShrink: 0
                          }}
                        >
                          {spoc.adminUserName.substring(0, 2).toUpperCase()}
                        </Avatar>
                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 16,
                              color: "#212121",
                              lineHeight: 1.2
                            }}
                          >
                            {spoc.adminUserName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#9CA3AF",
                              lineHeight: 1.4,
                              mt: 0.3
                            }}
                          >
                            {spoc.email} | {spoc.mobileNumber}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        sx={{ color: "#3A1C73", p: 0.5, flexShrink: 0 }}
                      >
                        <MoreVertIcon sx={{ fontSize: 24 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons Container - Full Width Strip */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 4,
          p: 3,
          bgcolor: "#fff",
          boxShadow: "0 -1px 3px rgba(0,0,0,0.08)",
          ml: { xs: -2, md: -3 },
          mr: { xs: -2, md: -3 },
          mb: { xs: -2, md: -3 },
          width: { xs: "calc(100% + 32px)", md: "calc(100% + 48px)" },
          pr: { xs: 3, md: 4 },
          position: "relative",
          left: { xs: -2, md: -3 },
          right: 0
        }}
      >
        <Button
          variant="outlined"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "#6B7280",
            borderColor: "#D1D5DB",
            textTransform: "none",
            px: 4,
            py: 1.2,
            borderRadius: 999,
            "&:hover": {
              borderColor: "#9CA3AF",
              bgcolor: "rgba(0,0,0,0.02)"
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "#fff",
            bgcolor: "#3A1C73",
            textTransform: "none",
            px: 4,
            py: 1.2,
            borderRadius: 999,
            "&:hover": {
              bgcolor: "#2e1560"
            }
          }}
        >
          Submit
        </Button>
      </Box>

      <AddOrganisationAdminModal
        open={adminModalOpen}
        onClose={() => setAdminModalOpen(false)}
        onAdminAdded={handleAdminAdded}
      />

      {/* SPOC Modal */}
      <AddSOPAdminModal
        open={spocModalOpen}
        onClose={() => setSpocModalOpen(false)}
        onAdminAdded={handleSpocAdded}
      />

      {/* Organisation Success Modal */}
      <OrganisationSuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        onRedirect={handleRedirect}
      />

      {/* View All SPOC Modal */}
      <ViewAllSpocModal
        open={viewAllSpocModalOpen}
        onClose={() => setViewAllSpocModalOpen(false)}
        spocList={spocDataList}
        onAddNew={() => {
          setViewAllSpocModalOpen(false);
          handleAddSpoc();
        }}
      />
    </Box>
  );
};

// Helper component for detail rows
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <Typography
      sx={{
        fontFamily: "Inter, sans-serif",
        fontWeight: 500,
        fontSize: 14,
        lineHeight: "20px",
        letterSpacing: "-0.005em",
        color: "#757575",
        flex: 0.5
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: "Inter, sans-serif",
        fontWeight: 500,
        fontSize: 14,
        lineHeight: "20px",
        letterSpacing: "-0.005em",
        color: "#212121",
        flex: 0.5,
        textAlign: "left"
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default OrganisationForm;
